import { useMemo, useState, useCallback } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import Footer from 'components/Footer';
import SearchIcon from 'components/icons/search';
import FacebookSolid from 'components/icons/facebookSolid';
import InstagramSolid from 'components/icons/instagramSolid';
import ChatbotIcon from 'components/icons/chatbotIcon';
import WhatsAppIcon from 'components/icons/whatsappIcon';
import ChatIcon from 'components/icons/chat';
import { ChannelOption, ChannelType } from 'components/ChannelPicker/types';
import LoadingShape from 'components/LoadingShape';
import ThreadsList from 'components/ThreadsList';
import useThreads from 'data/firestore/agent/thread/useCollection';
import useWebsiteChatbots from 'data/firestore/agent/widgetchatbot/useCollection';
import getDisplayName from 'data/services/profile/getDisplayName';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import { EntityGet } from 'data/firestore/types';
import { Thread } from 'data/firestore/agent/thread/types';
import useDebouncedValue from 'hooks/useDebouncedValue';
import useHttpMutation from 'data/firestore/useHttpMutation';
import ButtonIcon from 'components/ButtonIcon';
import Setting from 'components/icons/setting';
import TiktokIcon from 'components/icons/tiktokSolid';
import { HasRoles } from '@voxityai/auth/components';
import { auth } from 'data/firestore/firebase';
import TelegramIcon from 'components/icons/telegramSolid';
import { Provider } from './context';
import ConversationFilterPicker from './ConversationFilterPicker';
import {
    Container, LeftColumn, Main, FiltersContainer,
    StyledChannelPicker, Title, ThreadsLimitMessage, StyledSearchInput,
    ThreadsListContainer, NoConversationSelected, NoConversationIcon,
    NoConversationTitle, NoConversationDescription, TitleContainer,
    TitleActions,
} from './styled';
import buildFilters from './helpers/buildFilters';

const channels: ChannelOption[] = [{
    channel: 'whatsapp',
    label: 'WhatsApp',
    icon: <WhatsAppIcon />,
}, {
    channel: 'facebook',
    label: 'Facebook',
    icon: <FacebookSolid />,
}, {
    channel: 'instagram',
    label: 'Instagram',
    icon: <InstagramSolid />,
}, {
    channel: 'tiktok',
    label: 'TikTok',
    icon: <TiktokIcon />,
}, {
    channel: 'telegram',
    label: 'Telegram',
    icon: <TelegramIcon />,
}];

const Messages = () => {
    const { agentId, threadId } = useParams<{ agentId: string, threadId: string }>();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [channel, setChannel] = useState<ChannelType>('all');
    const [conversationFilter, setConversationFilter] = useState<string>('all');
    const navigate = useNavigateWithQuery();
    const navigateWithState = useNavigate();

    const searchQueryDebounced = useDebouncedValue(searchQuery, 500);

    const { mutateAsync: updateThread } = useHttpMutation({
        method: 'PATCH',
    });

    const {
        data: websiteChatbots,
        isLoading: isLoadingWebsiteChatbots,
    } = useWebsiteChatbots(agentId as string, {
        disabled: !agentId,
    });

    const { data: threads = [], isLoading: isLoadingThreads } = useThreads(agentId as string, {
        listQueryFn: (ref) => buildFilters({ ref, channel, conversationFilter }),
        track: [channel, agentId, conversationFilter],
        disabled: !agentId,
    });

    const isLoading = isLoadingWebsiteChatbots || isLoadingThreads;

    const availableChannels = useMemo(() => {
        if (!isLoadingWebsiteChatbots && websiteChatbots.length > 0) {
            return [
                ...channels,
                {
                    channel: 'widget_chatbot' as ChannelType,
                    label: 'Widget Chatbot',
                    icon: <ChatbotIcon />,
                },
            ];
        }
        return channels;
    }, [websiteChatbots, isLoadingWebsiteChatbots]);

    const filteredThreads = useMemo(() => {
        let filtered = threads;

        // Apply conversation filter
        if (conversationFilter === 'unread') {
            filtered = filtered.filter((thread) => thread.seenStatus === 'unseen');
        } else if (conversationFilter === 'ai-disabled') {
            filtered = filtered.filter((thread) => thread.aiResponseStatus === 'disabled');
        }

        // Apply search query filter
        if (searchQueryDebounced) {
            filtered = filtered.filter((thread) => {
                const displayName = getDisplayName(thread.customer).toLowerCase();
                return displayName.includes(searchQueryDebounced.toLowerCase());
            });
        }

        return filtered;
    }, [searchQueryDebounced, threads, conversationFilter]);

    const handleThreadSelect = useCallback(
        async (thread: EntityGet<Thread>) => {
            navigate(`/${agentId}/threads/${thread.id}`);
            try {
                updateThread({
                    pathname: `/agents/${agentId}/threads/${thread.id}`,
                    body: {
                        seenStatus: 'seen',
                    },
                });
            } catch (error) {
                // Silently fail if marking as seen fails
            }
        },
        [agentId, navigate, updateThread],
    );

    const handleUpdateSeenStatus = useCallback(
        async (updateThreadId: string, seenStatus: Thread['seenStatus']) => {
            if (!agentId) return;
            try {
                await updateThread({
                    pathname: `/agents/${agentId}/threads/${updateThreadId}`,
                    body: {
                        seenStatus,
                    },
                });
            } catch (error) {
                // Silently fail if updating seen status fails
            }
        },
        [agentId, updateThread],
    );

    return (
        <Provider value={{ websiteChatbots }}>
            <Container>
                <LeftColumn>
                    <TitleContainer>
                        <Title className="rainbow-m-left_x-small">Conversations</Title>
                        <TitleActions>
                            <ConversationFilterPicker
                                value={conversationFilter}
                                onChange={setConversationFilter}
                            />
                            <HasRoles auth={auth} allowedRoles={['admin']}>
                                <ButtonIcon
                                    icon={<Setting />}
                                    onClick={() => navigateWithState(`/${agentId}/settings/conversations/api-keys`, { state: { from: 'threads' } })}
                                />
                            </HasRoles>
                        </TitleActions>
                    </TitleContainer>
                    <FiltersContainer>
                        <StyledSearchInput
                            icon={<SearchIcon />}
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <StyledChannelPicker
                            value={channel}
                            onChange={setChannel}
                            channels={availableChannels}
                            hideOptionLabel
                        />
                    </FiltersContainer>
                    <ThreadsListContainer>
                        <RenderIf isTrue={!isLoading && threads.length > 0}>
                            <ThreadsLimitMessage>
                                Only the latest 3000 threads are displayed.
                            </ThreadsLimitMessage>
                        </RenderIf>
                        <ThreadsList
                            threads={filteredThreads}
                            isLoading={isLoading}
                            selectedThreadId={threadId}
                            onThreadSelect={handleThreadSelect}
                            onUpdateSeenStatus={handleUpdateSeenStatus}
                        />
                    </ThreadsListContainer>
                </LeftColumn>
                <Main>
                    <RenderIf isTrue={!threadId && isLoading}>
                        <div className="rainbow-flex rainbow-flex-grow rainbow-flex_column rainbow-p-around_small">
                            <LoadingShape height="0.75rem" width="150px" />
                            <LoadingShape height="0.75rem" width="300px" />
                            <LoadingShape height="0.75rem" width="200px" />
                        </div>
                    </RenderIf>
                    <RenderIf isTrue={!threadId && !isLoading}>
                        <NoConversationSelected>
                            <NoConversationIcon>
                                <ChatIcon />
                            </NoConversationIcon>
                            <NoConversationTitle>
                                No conversation selected
                            </NoConversationTitle>
                            <NoConversationDescription>
                                Click on any conversation on the list to view its messages
                            </NoConversationDescription>
                        </NoConversationSelected>
                    </RenderIf>
                    <RenderIf isTrue={!!threadId}>
                        <Outlet />
                    </RenderIf>
                </Main>
            </Container>
            <Footer />
        </Provider>
    );
};

export default Messages;
